import { useMemo, useState } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { api } from '../api';
import { useTranslation } from '../i18n/useTranslation';

interface WorkflowNodeDetailProps {
  node: Record<string, unknown> | null;
  onClose: () => void;
  accessStatus: string;
  canTrigger: boolean;
  workflowName: string;
  onTriggerSuccess?: () => void;
}

const NODE_TYPE_COLORS: Record<string, { accent: string; bg: string }> = {
  trigger: { accent: '#00734D', bg: 'rgba(0,115,77,0.08)' },
  // eslint-disable-next-line camelcase
  tool_call: { accent: '#27272A', bg: 'rgba(39,39,42,0.08)' },
  // eslint-disable-next-line camelcase
  ai_call: { accent: '#6366F1', bg: 'rgba(99,102,241,0.08)' },
  condition: { accent: '#CA8A04', bg: 'rgba(202,138,4,0.10)' },
  loop: { accent: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  transform: { accent: '#71717A', bg: 'rgba(113,113,122,0.08)' },
  // eslint-disable-next-line camelcase
  field_mapping: { accent: '#0EA5E9', bg: 'rgba(14,165,233,0.08)' },
  merge: { accent: '#E86E44', bg: 'rgba(232,110,68,0.08)' },
  visualization: { accent: '#0D9488', bg: 'rgba(13,148,136,0.08)' },
  data: { accent: '#9333EA', bg: 'rgba(147,51,234,0.08)' },
  parse: { accent: '#DB2777', bg: 'rgba(219,39,119,0.08)' },
};

const EXPRESSION_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.7rem',
        fontWeight: 600,
        mb: 0.5,
        display: 'block',
      }}
    >
      {children}
    </Typography>
  );
}

function Value({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <Typography
      variant="body2"
      sx={{
        mb: 2,
        wordBreak: 'break-word',
        fontFamily: mono ? '"SF Mono", "Fira Code", "Consolas", monospace' : undefined,
        fontSize: mono ? '0.8rem' : '0.875rem',
      }}
    >
      {children}
    </Typography>
  );
}

function Section({ title, hint, children }: { title?: string; hint?: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      {title && (
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>{title}</Typography>
      )}
      {hint && (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', mb: 1.5, lineHeight: 1.5 }}
        >
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function ExpressionChip({ path }: { path: string }) {
  const parts = path.split('.');
  const root = parts[0];
  let label = path;
  let color = '#71717A';
  if (root === 'trigger' && parts.length > 1) {
    label = `trigger · ${parts.slice(1).join('.')}`;
    color = '#0EA5E9';
  } else if ((root === 'nodes' || root === 'data') && parts.length > 1) {
    const nodeId = parts[1];
    const remainder = parts.slice(2).join('.').replace(/^output\./, '');
    label = `← ${nodeId}${remainder ? ' · ' + remainder : ''}`;
    color = '#0D9488';
  } else if (root === 'variables' && parts.length > 1) {
    label = `link · ${parts.slice(1).join('.')}`;
    color = '#9333EA';
  }
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 500,
        fontFamily: '"SF Mono", "Fira Code", monospace',
        letterSpacing: '-0.01em',
        bgcolor: `${color}14`,
        color,
        border: `1px solid ${color}33`,
      }}
    />
  );
}

function ExpressionAware({ value: rawValue, mono }: { value: unknown; mono?: boolean }) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return <span>—</span>;
  // Parameter values aren't always strings: hardcoded params can be arrays,
  // objects, booleans or numbers (e.g. files: [], open: false). Only strings
  // carry {{ }} expressions; coerce everything else to a readable literal so
  // the expression-matching below never runs .match on a non-string.
  const value = typeof rawValue === 'string' ? rawValue : JSON.stringify(rawValue);
  const pureMatch = value.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
  if (pureMatch) {
    return <ExpressionChip path={pureMatch[1].trim()} />;
  }
  if (!value.includes('{{')) {
    return (
      <Typography
        component="span"
        sx={{
          fontFamily: mono ? '"SF Mono", "Fira Code", monospace' : undefined,
          fontSize: mono ? '0.8rem' : '0.875rem',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    );
  }
  const segments: Array<{ kind: 'text' | 'expr'; value: string }> = [];
  let last = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(EXPRESSION_RE.source, 'g');
  while ((match = re.exec(value)) !== null) {
    if (match.index > last) segments.push({ kind: 'text', value: value.slice(last, match.index) });
    segments.push({ kind: 'expr', value: match[1].trim() });
    last = match.index + match[0].length;
  }
  if (last < value.length) segments.push({ kind: 'text', value: value.slice(last) });
  return (
    <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {segments.map((s, i) =>
        s.kind === 'text' ? (
          <Typography
            key={i}
            component="span"
            sx={{
              fontFamily: mono ? '"SF Mono", "Fira Code", monospace' : undefined,
              fontSize: mono ? '0.8rem' : '0.875rem',
            }}
          >
            {s.value}
          </Typography>
        ) : (
          <ExpressionChip key={i} path={s.value} />
        ),
      )}
    </Box>
  );
}

function LongTextField({
  value,
  mono,
  threshold = 500,
  maxLines = 10,
}: {
  value: string;
  mono?: boolean;
  threshold?: number;
  maxLines?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const lineCount = value.split('\n').length;
  const isLong = value.length > threshold || lineCount > maxLines;
  if (!isLong) return <ExpressionAware value={value} mono={mono} />;
  const preview = expanded ? value : value.slice(0, 200).trimEnd() + '…';
  return (
    <Box>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '8px',
          bgcolor: 'rgba(0,0,0,0.025)',
          border: '1px solid rgba(0,0,0,0.06)',
          maxHeight: expanded ? 360 : undefined,
          overflow: 'auto',
          fontFamily: mono ? '"SF Mono", "Fira Code", monospace' : undefined,
          fontSize: mono ? '0.78rem' : '0.85rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.6,
        }}
      >
        <ExpressionAware value={preview} mono={mono} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {value.length.toLocaleString('nl-NL')} tekens · {lineCount} regels
        </Typography>
        <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ textTransform: 'none' }}>
          {expanded ? 'Verbergen' : 'Toon volledig'}
        </Button>
      </Box>
    </Box>
  );
}

function SystemPromptField({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const lineCount = value.split('\n').length;
  const placeholders = (value.match(/\{\{[^}]+\}\}/g) || []).length;
  return (
    <Box>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '8px',
          bgcolor: 'rgba(99,102,241,0.05)',
          border: '1px solid rgba(99,102,241,0.15)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Typography variant="caption" sx={{ color: '#4f46e5', fontWeight: 600 }}>
          Systeem-instructie geconfigureerd
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {value.length.toLocaleString('nl-NL')} tekens · {lineCount} regels · {placeholders}{' '}
          placeholder{placeholders === 1 ? '' : 's'}
        </Typography>
        {expanded && (
          <Box
            sx={{
              mt: 1,
              p: 1.25,
              borderRadius: '6px',
              bgcolor: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              maxHeight: 320,
              overflow: 'auto',
              fontSize: '0.78rem',
              fontFamily: '"SF Mono", "Fira Code", monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.65,
            }}
          >
            <ExpressionAware value={value} mono />
          </Box>
        )}
      </Box>
      <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ mt: 0.5, textTransform: 'none' }}>
        {expanded ? 'Verberg instructie' : 'Toon instructie'}
      </Button>
    </Box>
  );
}

function ParametersTable({ params }: { params: Record<string, { source?: string; value?: unknown }> }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
      {Object.entries(params).map(([key, param]) => (
        <Box
          key={key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            py: 0.75,
            px: 1,
            borderRadius: '6px',
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.04)',
          }}
        >
          {/* Key + value stacked vertically: when the value is a long
              expression-chip ("← parse-import · output.payload.api") it
              would not fit next to a long key on one row; stacking keeps
              both readable. */}
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              fontWeight: 600,
              wordBreak: 'break-all',
            }}
          >
            {key}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
                minWidth: 32,
              }}
            >
              =
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ExpressionAware value={param.value} mono />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MappingsTable({ mappings }: { mappings: Array<{ from: string; to: string; transform?: { type?: string }; required?: boolean }> }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {mappings.map((m, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            py: 0.75,
            px: 1,
            borderRadius: '6px',
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.04)',
          }}
        >
          {/* From → To stacked vertically: monospace paths regularly exceed
              the drawer width. A single-row flex layout collides at the
              wrap boundary — long ``from`` wraps to a new line while the
              "→ to" cells stay on the original Y and overlap the
              continuation. Stacking removes the collision and keeps the
              relationship readable. */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
                minWidth: 32,
              }}
            >
              van
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                wordBreak: 'break-all',
                lineHeight: 1.4,
                flex: 1,
              }}
            >
              {m.from || <span style={{ color: '#aaa' }}>—</span>}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
                minWidth: 32,
              }}
            >
              naar
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                wordBreak: 'break-all',
                lineHeight: 1.4,
                flex: 1,
                fontWeight: 600,
              }}
            >
              {m.to || <span style={{ color: '#aaa' }}>—</span>}
            </Typography>
          </Box>
          {(m.transform?.type || m.required) && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, ml: 4.5 }}>
              {m.transform?.type && (
                <Chip
                  label={m.transform.type}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'monospace' }}
                />
              )}
              {m.required && (
                <Chip
                  label="vereist"
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(220,38,38,0.08)', color: '#dc2626' }}
                />
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function SchemaFieldsTable({ schema }: { schema: { properties?: Record<string, { type?: string; description?: string }>; required?: string[] } }) {
  const props = schema?.properties || {};
  const required = new Set(schema?.required || []);
  const entries = Object.entries(props);
  if (entries.length === 0) {
    return <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Geen parameters</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {entries.map(([name, prop]) => (
        <Box
          key={name}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            py: 0.75,
            px: 1,
            borderRadius: '6px',
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>{name}</Typography>
            <Chip label={prop.type || 'any'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
            {required.has(name) && <Chip label="vereist" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(220,38,38,0.08)', color: '#dc2626' }} />}
          </Box>
          {prop.description && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{prop.description}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function CopyJsonButton({ node }: { node: Record<string, unknown> }) {
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => JSON.stringify(node, null, 2), [node]);
  const handleCopy = () => {
    void navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <Tooltip title={copied ? 'Gekopieerd!' : 'Kopieer JSON voor support'}>
      <Button
        size="small"
        startIcon={copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
        onClick={handleCopy}
        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
      >
        Kopieer JSON
      </Button>
    </Tooltip>
  );
}

function TechnicalDetails({ node }: { node: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const data = (node.data as Record<string, unknown>) || {};
  const items: Array<{ k: string; v: unknown }> = [
    { k: 'node id', v: node.id },
    { k: 'serverId', v: data.serverId },
    { k: 'toolId', v: data.toolId },
    { k: 'parserId', v: data.parserId },
    { k: 'attachmentRole', v: data.attachmentRole },
  ].filter((it) => it.v !== undefined && it.v !== null && it.v !== '');
  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          size="small"
          onClick={() => setOpen(!open)}
          startIcon={open ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
        >
          Technische details
        </Button>
        <CopyJsonButton node={node} />
      </Box>
      {open && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((it) => (
            <Box key={it.k} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', minWidth: 100 }}>{it.k}</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.primary', wordBreak: 'break-all' }}>
                {String(it.v)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

interface RendererArgs {
  data: Record<string, unknown>;
  node: Record<string, unknown>;
  workflowName: string;
  accessStatus: string;
  canTrigger: boolean;
  onTrigger: () => Promise<void>;
  triggering: boolean;
  triggerResult: 'success' | 'error' | null;
  t: ReturnType<typeof useTranslation>;
}

function TriggerFields({ data, accessStatus, canTrigger, onTrigger, triggering, triggerResult, t }: RendererArgs) {
  const triggerType = (data.triggerType as string) || '—';
  const description = data.mcpToolDescription as string | undefined;
  const inputSchema = data.mcpToolInputSchema as { properties?: Record<string, { type?: string; description?: string }>; required?: string[] } | undefined;
  const hintByType: Record<string, string> = {
    cron: 'Deze workflow start automatisch volgens het onderstaande schema.',
    webhook: 'Deze workflow start zodra een extern systeem een POST op het webhook-pad doet.',
    manual: 'Deze workflow start handmatig vanuit de portal of via een API-call.',
    mcp: 'Deze workflow gedraagt zich als een MCP-tool. Een AI-client roept de tool aan met de onderstaande parameters.',
  };
  return (
    <Section hint={hintByType[triggerType]}>
      <Label>{t('workflow.nodeDetail.triggerType')}</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={triggerType} size="small" sx={{ fontWeight: 500 }} />
      </Box>
      {triggerType === 'cron' && (data.cronExpression as string) && (
        <>
          <Label>{t('workflow.nodeDetail.cronExpression')}</Label>
          <Value mono>{data.cronExpression as string}</Value>
        </>
      )}
      {triggerType === 'webhook' && (data.webhookPath as string) && (
        <>
          <Label>{t('workflow.nodeDetail.webhookPath')}</Label>
          <Value mono>{data.webhookPath as string}</Value>
        </>
      )}
      {triggerType === 'mcp' && (data.mcpToolName as string) && (
        <>
          <Label>MCP-tool naam</Label>
          <Value mono>{data.mcpToolName as string}</Value>
          {description && (
            <>
              <Label>Beschrijving</Label>
              <Value>{description}</Value>
            </>
          )}
          {inputSchema && (
            <>
              <Label>Verwachte parameters</Label>
              <SchemaFieldsTable schema={inputSchema} />
            </>
          )}
        </>
      )}
      {triggerType === 'manual' && accessStatus === 'active' && canTrigger && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={triggering ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={() => void onTrigger()}
            disabled={triggering}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('workflow.nodeDetail.triggerButton')}
          </Button>
          {triggerResult === 'success' && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
              {t('workflow.nodeDetail.triggerSuccess')}
            </Typography>
          )}
          {triggerResult === 'error' && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
              {t('workflow.nodeDetail.triggerError')}
            </Typography>
          )}
        </Box>
      )}
    </Section>
  );
}

function ToolCallFields({ data, t }: RendererArgs) {
  const parameters = data.parameters as Record<string, { source?: string; value?: string }> | undefined;
  const serverName = (data.serverName as string) || '—';
  const toolName = (data.toolName as string) || '—';
  return (
    <Section hint={`Roept tool "${toolName}" aan op ${serverName}. Waarden met "←" komen uit een eerdere node.`}>
      <Label>{t('workflow.nodeDetail.server')}</Label>
      <Value>{serverName}</Value>
      <Label>{t('workflow.nodeDetail.tool')}</Label>
      <Value mono>{toolName}</Value>
      {parameters && Object.keys(parameters).length > 0 && (
        <>
          <Label>{t('workflow.nodeDetail.parameters')}</Label>
          <ParametersTable params={parameters} />
        </>
      )}
    </Section>
  );
}

function AiCallFields({ data }: RendererArgs) {
  const model = (data.model as string) || '—';
  const systemPrompt = (data.systemPrompt as string) || '';
  const userPrompt = (data.userPrompt as string) || '';
  const inputMapping = data.inputMapping as Record<string, { source?: string; value?: string }> | undefined;
  const temperature = data.temperature as number | undefined;
  const expectJson = data.expectJson as boolean | undefined;
  return (
    <Section hint="Stuurt een prompt naar het taalmodel. Variabelen tussen accolades worden ingevuld met data uit eerdere stappen.">
      <Label>Model</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={model} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        {temperature !== undefined && <Chip label={`temp ${temperature}`} size="small" sx={{ ml: 0.5, height: 22, fontSize: '0.7rem' }} />}
        {expectJson && <Chip label="JSON" size="small" sx={{ ml: 0.5, height: 22, fontSize: '0.7rem' }} />}
      </Box>
      {systemPrompt && (
        <>
          <Label>Systeem-instructie</Label>
          <Box sx={{ mb: 2 }}>
            <SystemPromptField value={systemPrompt} />
          </Box>
        </>
      )}
      {userPrompt && (
        <>
          <Label>User-prompt</Label>
          <Box sx={{ mb: 2 }}>
            <LongTextField value={userPrompt} mono />
          </Box>
        </>
      )}
      {inputMapping && Object.keys(inputMapping).length > 0 && (
        <>
          <Label>Input mapping</Label>
          <ParametersTable params={inputMapping} />
        </>
      )}
    </Section>
  );
}

function DataFields({ data }: RendererArgs) {
  const format = (data.format as string) || 'xml';
  const selector = (data.selector as string) || '';
  const role = (data.attachmentRole as string) || '';
  const attachmentLabel = (data.attachmentLabel as string) || '';
  const attachmentDescription = (data.attachmentDescription as string) || '';
  return (
    <Section hint={attachmentLabel ? `Selecteert een stuk data uit het geüploade "${attachmentLabel}" bestand.` : 'Selecteert een stuk data uit een geüpload bestand.'}>
      {attachmentLabel && (
        <>
          <Label>Bestand</Label>
          <Value>{attachmentLabel}</Value>
        </>
      )}
      {attachmentDescription && (
        <>
          <Label>Beschrijving</Label>
          <Value>{attachmentDescription}</Value>
        </>
      )}
      <Label>Formaat</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={format} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
      </Box>
      {selector && (
        <>
          <Label>Selector ({format === 'xml' ? 'XPath' : 'dot-pad'})</Label>
          <Box sx={{ mb: 2 }}>
            <LongTextField value={selector} mono threshold={120} maxLines={3} />
          </Box>
        </>
      )}
      {role && (
        <>
          <Label>Slot-naam</Label>
          <Box sx={{ mb: 1 }}>
            <Chip label={role} size="small" sx={{ fontFamily: 'monospace', height: 22 }} />
          </Box>
        </>
      )}
    </Section>
  );
}

function ParseFields({ data }: RendererArgs) {
  const parserId = (data.parserId as string) || '';
  const mode = (data.mode as string) || 'parse';
  const inputSource = (data.inputSource as string) || '';
  const extra = data.extra as Record<string, { source?: string; value?: string }> | undefined;
  return (
    <Section hint={mode === 'requirements' ? 'Voert een dry-run uit en rapporteert welke velden nog ontbreken om succesvol te kunnen importeren.' : 'Zet ruwe data om naar een gestructureerd payload-object voor de volgende stap.'}>
      <Label>Parser</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={parserId || '—'} size="small" sx={{ fontFamily: 'monospace' }} />
      </Box>
      <Label>Modus</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={mode} size="small" variant="outlined" />
      </Box>
      {inputSource && (
        <>
          <Label>Bron-data</Label>
          <Box sx={{ mb: 2 }}>
            <ExpressionAware value={`{{ ${inputSource} }}`} mono />
          </Box>
        </>
      )}
      {extra && Object.keys(extra).length > 0 && (
        <>
          <Label>Extra configuratie</Label>
          <ParametersTable params={extra} />
        </>
      )}
    </Section>
  );
}

function FieldMappingFields({ data }: RendererArgs) {
  const inputSource = (data.inputSource as string) || '';
  const mappings = (data.mappings as Array<{ from: string; to: string; transform?: { type?: string }; required?: boolean }>) || [];
  return (
    <Section hint="Mapt brongegevens naar doelvelden. Pas optioneel een transformatie toe per veld.">
      {inputSource && (
        <>
          <Label>Bron</Label>
          <Box sx={{ mb: 2 }}>
            <ExpressionAware value={`{{ ${inputSource} }}`} mono />
          </Box>
        </>
      )}
      <Label>Mappings ({mappings.length})</Label>
      {mappings.length > 0 ? <MappingsTable mappings={mappings} /> : <Value>—</Value>}
    </Section>
  );
}

function ConditionFields({ data, t }: RendererArgs) {
  const expression = (data.expression as string) || '';
  return (
    <Section hint="Volgt het true-pad als de expressie waar is, anders het false-pad.">
      <Label>{t('workflow.nodeDetail.expression')}</Label>
      <Box sx={{ mb: 1 }}>
        {expression ? <ExpressionAware value={expression} mono /> : <Value>—</Value>}
      </Box>
    </Section>
  );
}

function LoopFields({ data, t }: RendererArgs) {
  const arrayExpression = (data.arrayExpression as string) || '';
  const itemVariable = (data.itemVariable as string) || '';
  return (
    <Section hint={itemVariable ? `Herhaalt de volgende stappen voor elk item, beschikbaar als "${itemVariable}".` : 'Herhaalt de volgende stappen voor elk item uit de array.'}>
      <Label>{t('workflow.nodeDetail.arrayExpression')}</Label>
      <Box sx={{ mb: 2 }}>
        {arrayExpression ? <ExpressionAware value={arrayExpression} mono /> : <Value>—</Value>}
      </Box>
      <Label>{t('workflow.nodeDetail.itemVariable')}</Label>
      <Value mono>{itemVariable || '—'}</Value>
    </Section>
  );
}

function TransformFields({ data, t }: RendererArgs) {
  const mappings = (data.mappings as Array<{ from: string; to: string }>) || [];
  return (
    <Section hint="Hernoemt of herstructureert velden voor de volgende stap.">
      <Label>{t('workflow.nodeDetail.mappings')}</Label>
      {mappings.length > 0 ? <MappingsTable mappings={mappings} /> : <Value>—</Value>}
    </Section>
  );
}

function MergeFields({ data, t }: RendererArgs) {
  const strategy = (data.strategy as string) || '—';
  return (
    <Section hint="Voegt resultaten van parallelle paden samen voordat de volgende stap start.">
      <Label>{t('workflow.nodeDetail.strategy')}</Label>
      <Box>
        <Chip label={strategy} size="small" variant="outlined" />
      </Box>
    </Section>
  );
}

function VisualizationFields({ data }: RendererArgs) {
  const visualizationType = (data.visualizationType as string) || 'auto';
  const sourceExpression = (data.sourceExpression as string) || '';
  return (
    <Section hint="Toont het resultaat van de gekozen bron als tabel of grafiek in het portaal.">
      <Label>Type</Label>
      <Box sx={{ mb: 2 }}>
        <Chip label={visualizationType} size="small" variant="outlined" />
      </Box>
      {sourceExpression && (
        <>
          <Label>Bron</Label>
          <Box sx={{ mb: 1 }}>
            <ExpressionAware value={`{{ ${sourceExpression} }}`} mono />
          </Box>
        </>
      )}
    </Section>
  );
}

export default function WorkflowNodeDetail({
  node,
  onClose,
  accessStatus,
  canTrigger,
  workflowName,
  onTriggerSuccess,
}: WorkflowNodeDetailProps) {
  const { getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<'success' | 'error' | null>(null);

  if (!node) return null;

  const data = (node.data as Record<string, unknown>) || {};
  const label = (data.label as string) || (node.id as string) || '';
  const nodeType = (data.type as string) || 'unknown';
  const accent = NODE_TYPE_COLORS[nodeType] || { accent: '#71717A', bg: 'rgba(113,113,122,0.08)' };

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = await getAccessTokenSilently();
      await api.triggerWorkflow(token, workflowName);
      setTriggerResult('success');
      onTriggerSuccess?.();
    } catch {
      setTriggerResult('error');
    } finally {
      setTriggering(false);
    }
  };

  const rendererArgs: RendererArgs = {
    data,
    node,
    workflowName,
    accessStatus,
    canTrigger,
    onTrigger: handleTrigger,
    triggering,
    triggerResult,
    t,
  };

  const renderFields = () => {
    switch (nodeType) {
      case 'trigger':
        return <TriggerFields {...rendererArgs} />;
      case 'tool_call':
        return <ToolCallFields {...rendererArgs} />;
      case 'ai_call':
        return <AiCallFields {...rendererArgs} />;
      case 'condition':
        return <ConditionFields {...rendererArgs} />;
      case 'loop':
        return <LoopFields {...rendererArgs} />;
      case 'transform':
        return <TransformFields {...rendererArgs} />;
      case 'field_mapping':
        return <FieldMappingFields {...rendererArgs} />;
      case 'merge':
        return <MergeFields {...rendererArgs} />;
      case 'visualization':
        return <VisualizationFields {...rendererArgs} />;
      case 'data':
        return <DataFields {...rendererArgs} />;
      case 'parse':
        return <ParseFields {...rendererArgs} />;
      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={!!node}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, p: 3, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75, wordBreak: 'break-word' }}>{label}</Typography>
          <Chip
            label={nodeType}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              bgcolor: accent.bg,
              color: accent.accent,
              border: `1px solid ${accent.accent}33`,
              fontFamily: '"SF Mono", "Fira Code", monospace',
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {renderFields()}
      </Box>

      <TechnicalDetails node={node} />
    </Drawer>
  );
}
